/**
 * Proportional sizing for in-figure chrome.
 *
 * Every token in ./layout is an ABSOLUTE pixel size, but the figure they are
 * drawn into is not a fixed box: a dashboard card measures ~650x390 CSS px while
 * a report figure is pinned to REPORT_FIGURE (1000x600). Holding the type at 17px
 * across both means the title occupies 2.6% of the card's width and 1.7% of the
 * report's — the same chart reads as two different documents, and the published
 * PDF (which scales that 1000px PNG down into a text column) came out with 11pt
 * titles. Measured on the scatter, the two canvases differ by a uniform 1.54x in
 * BOTH axes (aspect 1.672 vs 1.667), so a single scale reconciles them; no layout
 * rework is involved.
 *
 * This is also the root the text-fitting in ../shared/text only compensates for.
 * Wrapping and the font step-down exist because absolute type overflows a narrow
 * container; with the type scaling to the container they become what they should
 * always have been — a safety net for extreme aspect ratios, not the mechanism.
 */

/**
 * Figure width (CSS px) at which the ./layout tokens render exactly as authored.
 *
 * 650 is the dashboard card's measured graph div (648x388), which is what those
 * sizes were tuned against. Anchoring here rather than at REPORT_FIGURE.width is
 * deliberate: it keeps the on-screen card pixel-identical to what it is today and
 * scales the report UP to match it, instead of shrinking every card to match the
 * report.
 */
export const TYPO_REFERENCE_WIDTH = 650;

/**
 * Bounds on the scale factor.
 *
 * `min` keeps a chart in a narrow grid column readable: a 300px card would ask for
 * 0.46 and put the chrome at 6px. Below this floor the type stops shrinking and
 * ../shared/text takes over — wrapping and stepping down only what still doesn't
 * fit. `max` stops an unusually wide container (a full-bleed 2500px dashboard)
 * from rendering a poster-sized title.
 */
export const TYPO_SCALE_RANGE = { min: 0.75, max: 1.6 } as const;

/**
 * Scale factor for a figure of `figureWidth` CSS px.
 *
 * Returns 1 when the width is unknown (<= 0 or undefined), matching the same
 * "don't guess before the container is measured" contract the text fitting
 * follows. ChartRenderer measures before its first draw (SPEC/2539), so this only
 * applies where nothing can be measured (no ResizeObserver, or a 0x0 container).
 */
export function figureScale(figureWidth?: number): number {
  if (!figureWidth || figureWidth <= 0) return 1;
  const raw = figureWidth / TYPO_REFERENCE_WIDTH;
  return Math.min(TYPO_SCALE_RANGE.max, Math.max(TYPO_SCALE_RANGE.min, raw));
}

/**
 * A font token with its `size` scaled, everything else (family, color, weight)
 * untouched. Kept to ONE decimal: the chrome token is 12.5px, and rounding to an
 * integer would re-tune it at every scale instead of just resizing it.
 */
export function scaleFont<T extends { size: number }>(font: T, scale: number): T {
  return { ...font, size: Math.round(font.size * scale * 10) / 10 };
}

/** A pixel metric (margin, pad, tick length) scaled and rounded to a whole pixel. */
export function scalePx(value: number, scale: number): number {
  return Math.round(value * scale);
}
